import { applyCardListFilters, resetCardListFilters, toggleFilterChip, clearChipForHideFilter } from "../card_list_filter.js";
import { handleCollectionUpdateRequest, readRowQuantities } from "../save_card.js";
import { copyCardList } from "./copy_card_list.js";
import { initBulkActions } from "./bulk_actions.js";
import { initChipScroller } from "../chip_scroller.js";

function groupRows(group){
    return [...group.querySelectorAll('.card-rows > .card-row')];
}

export function updateGroupProgress(root, group){
    const rows = groupRows(group);
    const total = Number(group.dataset.cgCardCount) || rows.length;
    const owned = rows.filter(row => Number(row.dataset.bsOwned) > 0).length;
    const complete = total > 0 && owned >= total;
    const percent = total > 0 ? Math.min(100, Math.round((owned / total) * 100)) : 0;

    group.classList.toggle('completed', complete);
    const fill = group.querySelector('.card-group-progress-fill');
    if (fill){
        fill.style.width = `${percent}%`;
    }
    const bar = group.querySelector('.card-group-progress-bar');
    if (bar){
        bar.setAttribute('aria-valuenow', owned);
    }
    const text = group.querySelector('[data-progress-text]');
    if (text){
        text.textContent = `${owned}/${total}`;
    }
    const trophy = group.querySelector('.card-group-trophy');
    if (trophy){
        trophy.hidden = !complete;
    }
    const jumpLink = [...root.querySelectorAll('.card-list-jump-link')].find(link => link.getAttribute('href') === `#${group.id}`);
    if (jumpLink && !root.classList.contains('card-list-public')){
        jumpLink.classList.toggle('is-complete', complete);
        jumpLink.querySelector('.card-list-jump-count').textContent = `${owned}/${total}`;
    }
}

export function updateAllProgress(root){
    root.querySelectorAll('.card-group-item').forEach(group => updateGroupProgress(root, group));
}

function buildJumpNav(root){
    const nav = root.querySelector('[data-card-list-jump]');
    if (!nav){
        return;
    }
    root.querySelectorAll('.card-group-item').forEach(group => {
        const link = document.createElement('a');
        link.className = 'card-list-jump-link';
        link.href = `#${group.id}`;
        const label = document.createElement('span');
        label.textContent = group.dataset.jumpLabel;
        const count = document.createElement('span');
        count.className = 'card-list-jump-count';
        link.append(label, count);
        nav.append(link);
    });
}

function syncJumpVisibility(root){
    root.querySelectorAll('.card-list-jump-link').forEach(link => {
        const group = document.getElementById(link.getAttribute('href').slice(1));
        link.hidden = !group || group.hidden;
    });
}

// Group headers stick just under the toolbar, whose height changes as it wraps
function trackToolbarHeight(root){
    const toolbar = root.querySelector('[data-card-list-toolbar]');
    if (!toolbar){
        return;
    }
    const update = () => root.style.setProperty('--card-list-sticky-top', `${toolbar.offsetHeight}px`);
    update();
    if (typeof ResizeObserver !== 'undefined'){
        new ResizeObserver(update).observe(toolbar);
    }
}

export function initCardList(root, { saveTimers = new Map(), isPublic = false, readOnly = false } = {}){
    // Public lists have no marks at all; read-only lists show someone else's marks without letting you change them
    const canEdit = !isPublic && !readOnly;
    const toolbar = root.querySelector('[data-card-list-toolbar]');
    const refreshFilters = () => {
        applyCardListFilters(root);
        syncJumpVisibility(root);
    };

    buildJumpNav(root);
    trackToolbarHeight(root);
    root.querySelectorAll('[data-chip-scroller]').forEach(initChipScroller);

    root.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-filter-key]');
        if (chip){
            toggleFilterChip(root, chip);
            refreshFilters();
            return;
        }
        if (event.target.closest('[data-clear-filters]')){
            resetCardListFilters(root);
            syncJumpVisibility(root);
            return;
        }
        const copyButton = event.target.closest('[data-copy-format]');
        if (copyButton){
            copyCardList(root, copyButton.dataset.copyFormat, { isPublic });
        }
    });

    const searchInput = root.querySelector('[data-card-search]');
    if (searchInput){
        searchInput.addEventListener('input', refreshFilters);
    }

    root.addEventListener('change', (event) => {
        if (toolbar && toolbar.contains(event.target)){
            if (event.target.matches('[data-hide-filter]')){
                clearChipForHideFilter(root, event.target);
            }
            refreshFilters();
            return;
        }
        const row = event.target.closest('.card-rows > .card-row');
        if (!canEdit || !row){
            return;
        }
        const quantities = readRowQuantities(row);
        row.dataset.bsOwned = quantities.owned_count;
        row.dataset.bsTrade = quantities.trade_count;
        row.dataset.bsWanted = quantities.wanted_count;
        row.classList.toggle('is-owned', quantities.owned_count > 0);
        updateGroupProgress(root, row.closest('.card-group-item'));
        handleCollectionUpdateRequest(event, saveTimers);
    });

    // An open bulk menu has to sit above the next group's sticky header
    root.addEventListener('show.bs.dropdown', (event) => {
        const header = event.target.closest('.card-group-header');
        if (header){
            header.classList.add('is-menu-open');
        }
    });
    root.addEventListener('hidden.bs.dropdown', (event) => {
        const header = event.target.closest('.card-group-header');
        if (header){
            header.classList.remove('is-menu-open');
        }
    });

    if (canEdit){
        initBulkActions(root, { saveTimers, onApplied: () => updateAllProgress(root) });
    }

    const refresh = () => {
        updateAllProgress(root);
        refreshFilters();
    };
    refresh();
    return { refresh };
}
